import {
  DeleteOutlined,
  EllipsisOutlined,
  ExclamationCircleOutlined,
  FilterFilled,
} from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Button,
  Card,
  Dropdown,
  Form,
  Modal,
  Radio,
  Space,
  Tag,
  Tooltip,
  message,
} from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import {
  DEFAULT_DATE_FORMAT,
  MODULES_KEY,
  PERMISSION_OBJECT,
  PROJECTS_TABLE_FIELDS,
  ROUTES_MODULES_KEY,
} from '../../common/constants';
import CommonTable from '../../components/CommonTable';
import SearchComponent from '../../components/SearchComponent';
import VerifyPermissions from '../../components/VerifyPermissions';
import {
  antdSortOrder,
  mapAntTableChangeToParams,
  useTableQueryParams,
} from '../../hooks/useTableQueryParams';
import CreateSheetModal from './components/CreateSheetModal';
import ProjectModal from './components/ProjectModal';
import { DELETE_PROJECT } from './graphql/Mutations';
import { PROJECTS } from './graphql/Queries';

const buildProjectsFilter = (params) => ({
  skip: (params?.page - 1) * params?.pageSize,
  limit: params?.pageSize,
  searchString: params?.search,
  sortOrder: params?.sortOrder,
  sortField: params?.sortField,
  status: params?.status,
});

function Projects() {
  const [form] = Form?.useForm();
  const history = useHistory();
  const { params, setParams } = useTableQueryParams({
    fields: PROJECTS_TABLE_FIELDS,
  });
  const [showModal, setShowModal] = useState(false);
  const [showCreateSheetModal, setShowCreateSheetModal] = useState(false);
  const [projectRecord, setProjectRecord] = useState();
  const [statusFilterVisible, setStatusFilterVisible] = useState(false);
  const [draftFilter, setDraftFilter] = useState({ status: params?.status });
  const isCreatePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.PROJECTS,
    allowedPermissions: [PERMISSION_OBJECT?.create],
  });
  const isDeletePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.PROJECTS,
    allowedPermissions: [PERMISSION_OBJECT?.delete],
  });
  useEffect(() => {
    const search = new URLSearchParams(history?.location?.search);
    const flashMessage = search?.get('message');
    if (flashMessage) {
      message.info({
        content: decodeURIComponent(flashMessage),
        duration: 2,
        onClose: () => {
          search?.delete('message');
          history?.replace({
            pathname: history?.location?.pathname,
            search: search?.toString(),
          });
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [executeQuery, { loading, data }] = useLazyQuery(PROJECTS, {
    fetchPolicy: 'network-only',
    onError() {},
    refetchQueries: [
      {
        query: PROJECTS,
        variables: {
          filters: buildProjectsFilter(params),
        },
      },
    ],
  });

  useEffect(() => {
    executeQuery({ variables: { filters: buildProjectsFilter(params) } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const [deleteProject] = useMutation(DELETE_PROJECT, {
    fetchPolicy: 'network-only',
    onCompleted: () => {
      executeQuery({
        variables: { filters: buildProjectsFilter({ ...params, page: 1 }) },
      });
      setParams({ page: 1 });
    },
    onError: (error) => message?.error(`Error! ${error?.message}`),
  });

  const handleDeleteClick = (record) => {
    Modal?.confirm({
      icon: <ExclamationCircleOutlined />,
      title: 'Delete Project',
      content: 'Are you sure you want to delete this project?',
      centered: true,
      closable: false,
      okText: 'Yes',
      cancelText: 'No',
      okButtonProps: { danger: true },
      className: 'custom-modal-button',
      onOk: () => deleteProject({ variables: { where: { id: record?.id } } }),
    });
  };

  const handleSearch = (value) => {
    setParams({ search: value?.trim(), page: 1 });
  };

  const handleAddProject = () => {
    setProjectRecord(null);
    setShowModal(true);
  };

  const handleCreateSheet = () => {
    setShowCreateSheetModal(true);
  };

  const onChange = (pagination, _filters, sorter) => {
    setParams(mapAntTableChangeToParams(pagination, sorter, params?.pageSize));
  };

  const handleReset = () => {
    form?.resetFields();
    setDraftFilter((prev) => ({ ...prev, status: null }));
    setParams({ status: null });
  };
  const handleStatus = (e) => {
    setDraftFilter((prev) => ({ ...prev, status: e?.target?.value }));
  };
  const handleFilter = () => {
    setParams({ status: draftFilter?.status, page: 1 });
    setStatusFilterVisible(false);
  };

  const getProjectFilterProps = () => ({
    filterDropdownOpen: statusFilterVisible,
    onFilterDropdownOpenChange: (visible) => setStatusFilterVisible(visible),
    filterDropdown: () => (
      <div className="columnFilter" onKeyDown={(e) => e?.stopPropagation()}>
        <Space direction="vertical">
          <Form name="control-ref" form={form} layout="vertical">
            <Form.Item name="status" valuePropName="checked">
              <Radio.Group
                defaultValue={draftFilter?.status}
                onChange={handleStatus}
                value={draftFilter?.status}
              >
                <Space direction="vertical">
                  <Radio value="ACTIVE">Active</Radio>
                  <Radio value="INACTIVE">Inactive</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          </Form>
          <Space direction="horizontal">
            <Button size="small" onClick={handleFilter}>
              Apply
            </Button>
            <Button
              danger
              size="small"
              onClick={() => {
                handleReset();
                setStatusFilterVisible(false);
              }}
            >
              Reset
            </Button>
          </Space>
        </Space>
      </div>
    ),
    render: (text, record) => (
      <Link
        to={`${ROUTES_MODULES_KEY?.PROJECTS}/${record?.id}`}
        onClick={(e) => e?.stopPropagation()}
      >
        <Tag
          className="project-table-tags"
          color={record?.status?.length > 6 ? 'error' : 'success'}
        >
          {text?.[0]?.toUpperCase() + text?.slice(1)?.toLowerCase()}
        </Tag>
      </Link>
    ),
    filterIcon: () => (
      <FilterFilled
        className={
          draftFilter?.status || params?.status ? 'filter-filled-data' : ''
        }
      />
    ),
  });

  const handleViewReleases = (record) => {
    history.push({
      pathname: `${ROUTES_MODULES_KEY?.RELEASES}`,
      state: {
        fromListing: true,
        projectIds: [record?.id],
        projectName: record?.name,
      },
    });
  };

  const handleViewAppBuilds = (record) => {
    history.push({
      pathname: `${ROUTES_MODULES_KEY?.APP_BUILDS}`,
      state: {
        fromListing: true,
        projectId: record?.id,
        projectName: record?.name,
      },
    });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: antdSortOrder(params, 'name'),
      className: 'link-custom-width',
      render: (value, record) => (
        <Link
          to={`${ROUTES_MODULES_KEY?.PROJECTS}/${record?.id}`}
          onClick={(e) => e?.stopPropagation()}
        >
          {record?.name}
        </Link>
      ),
    },
    {
      title: 'Client Name',
      dataIndex: 'clientName',
      key: 'clientName',
      className: 'link-custom-width',
      render: (value, record) => (
        <Link
          to={`${ROUTES_MODULES_KEY?.PROJECTS}/${record?.id}`}
          onClick={(e) => e?.stopPropagation()}
        >
          {record?.clientName}
        </Link>
      ),
    },
    {
      title: 'Created On',
      dataIndex: 'createdOn',
      key: 'createdOn',
      sortOrder: antdSortOrder(params, 'createdOn'),
      sortDirections: ['descend'],
      sorter: true,
      className: 'link-custom-width',
      render: (value, record) => (
        <Link
          to={`${ROUTES_MODULES_KEY?.PROJECTS}/${record?.id}`}
          onClick={(e) => e?.stopPropagation()}
        >
          {dayjs(record?.createdOn)?.format(DEFAULT_DATE_FORMAT)}
        </Link>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      filterMultiple: false,
      ...getProjectFilterProps(),
      className: 'link-custom-width',
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 100,
      render: (value, record) => (
        <Space size="middle">
          <Dropdown
            menu={{
              items: [
                { key: 'viewReleases', label: 'View Releases' },
                ...(VerifyPermissions(MODULES_KEY?.APP_BUILDS)
                  ? [{ key: 'viewAppBuilds', label: 'View App Builds' }]
                  : []),
              ],
              onClick: ({ key, domEvent }) => {
                domEvent?.stopPropagation();
                if (key === 'viewReleases') {
                  handleViewReleases(record);
                }
                if (key === 'viewAppBuilds') {
                  handleViewAppBuilds(record);
                }
              },
            }}
            trigger={['click']}
          >
            <Button
              type="text"
              icon={<EllipsisOutlined />}
              onClick={(e) => e?.stopPropagation()}
            />
          </Dropdown>
          {isDeletePermission && (
            <Tooltip placement="bottom" title={<span>Delete Project</span>}>
              <Button
                danger
                type="text"
                icon={<DeleteOutlined />}
                onClick={(e) => {
                  e?.stopPropagation();
                  handleDeleteClick(record);
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];
  const handleRowClick = (record) => {
    history?.push(`${ROUTES_MODULES_KEY?.PROJECTS}/${record?.id}`);
  };

  return (
    <>
      <div className="table-card-page">
        <Card
          className="ant-body-scroll "
          title="Projects"
          extra={
            <div className="project-filter-right">
              <div className="project-filter">
                {isCreatePermission && (
                  <>
                    <Button key="1" type="primary" onClick={handleCreateSheet}>
                      Create Sheet
                    </Button>
                    <Button key="2" type="primary" onClick={handleAddProject}>
                      Add Project
                    </Button>
                  </>
                )}
                <SearchComponent
                  key={params?.search || ''}
                  defaultValue={params?.search}
                  getData={handleSearch}
                  name="Project Or Client"
                />
              </div>
            </div>
          }
        >
          <div className="card-body-wrapper">
            <CommonTable
              size="small"
              columns={columns}
              dataSource={data?.projects?.projects}
              onChange={onChange}
              rowKey={(obj) => obj?.id}
              loadingData={loading}
              bordered
              paginationConfig={{
                current: params?.page,
                total: data?.projects?.count,
                pageSize: params?.pageSize,
              }}
              onRow={(record) => ({ onClick: () => handleRowClick(record) })}
              rowClassName="project-table-row"
              className="project-table"
            />
            <ProjectModal
              projectRecord={projectRecord}
              setProjectRecord={setProjectRecord}
              executeQuery={executeQuery}
              show={showModal}
              close={() => setShowModal(false)}
            />
            {showCreateSheetModal && (
              <CreateSheetModal
                show={showCreateSheetModal}
                close={() => setShowCreateSheetModal(false)}
              />
            )}
          </div>
        </Card>
      </div>
    </>
  );
}

export default Projects;
